import { useLocation, useParams } from 'react-router-dom'

import { AdjustEditorProvider } from './adjust/AdjustEditor.context'
import { ClientPhotoStudioContextProvider } from './ClientPhotoStudio.context'
import { ClientPhotoStudioController } from './ClientPhotoStudioController.controller'
import { FC, useEffect } from 'react'
import { PageTransition } from 'utils/animations'
import { PhotoStudioEmptyContent } from './PhotoStudioEmptyContent/PhotoStudioEmptyContent.component'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'

export const ClientPhotoStudio: FC = () => {
  const { t } = useTranslation(['photo_studio_editing'])
  const location = useLocation()
  const params = useParams<{ id?: string, assignmentId?: string }>()
  const { spawnErrorToast } = useSnackbar()

  const state = location.state as { file?: File, imageUrl?: string, assignmentId?: string, isSelfEditedVisual?: boolean } | null

  // Check if state exists and has required properties
  const hasValidState = state && state.file && state.imageUrl && !!state.assignmentId

  // Get assignment ID and property ID from URL params
  // Route structure: /property/:id/gallery/:assignmentId/photo-studio
  // So params.id is propertyId and params.assignmentId is assignmentId
  const assignmentId = params.assignmentId
  const propertyId = params.id

  const file = hasValidState ? state.file : undefined
  const hasFileName = !!file?.name

  useEffect(() => {
    if (hasValidState && !hasFileName) {
      spawnErrorToast(t('error_missing_visual_name'))
      window.history.back()
    }
  }, [hasValidState, hasFileName, spawnErrorToast, t])

  // If state is missing, show empty content
  if (!hasValidState) {
    return <PhotoStudioEmptyContent assignmentId={assignmentId} propertyId={propertyId} />
  }

  if (!hasFileName) {
    return null
  }

  const { imageUrl, assignmentId: stateAssignmentId, isSelfEditedVisual } = state

  return (
    <ClientPhotoStudioContextProvider file={file!} imageUrl={imageUrl!} assignmentId={stateAssignmentId!} isSelfEditedVisual={isSelfEditedVisual ?? false}>
      {/* The in-house Adjust editor's state, shared by its panel and its canvas (inert while its flag is off) */}
      <AdjustEditorProvider>
        <PageTransition>

          <ClientPhotoStudioController />

        </PageTransition>
      </AdjustEditorProvider>
    </ClientPhotoStudioContextProvider>
  )
}
